// Pinia store for the Moria project dashboard.
//
// Responsibilities:
//   - Own native project records and their editable metadata.
//   - Talk to the backend via the generated ProjectsService; all calls use the
//     shared OpenAPI singleton which is configured for cookie-based auth.
// Any error thrown by a service call is surfaced on the store's `error` field
// (last-write-wins) and also re-thrown so callers can react (e.g. toasts). The
// generated client throws `ApiError` which carries `status` + `message`; we
// normalize plain-object throwables the same way the auth store does so tests
// can inject simple fixtures.
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { AdminService, ProjectsService } from '@/api'
import type {
  CustomerAccessOut,
  CustomerProjectSummaryOut,
  ProjectListItem,
  ProjectOverviewStatsOut,
  ProjectOut,
  ProjectPatch,
  ProjectQaQcSettings,
  ProjectQaQcSettingsUpdate,
} from '@/api'
import { classifyApiError, extractErrorMessage } from '@/utils/apiError'
import { projectFeatures, type ProjectFeatureSupport } from '@/domain/projectFeatures'
import type { ErrorKind } from '@/utils/apiError'

export interface ProjectsQuery {
  search: string
  stage: string
  sort: string
  page: number
  pageSize: number
  includeArchived: boolean
}

export const DEFAULT_PROJECTS_QUERY: ProjectsQuery = {
  search: '',
  // Default to active work. Completion and archival are independent.
  // `'all'` is the explicit
  // sentinel for "no filter"; the backend ignores unknown stage values so the
  // sentinel survives the round-trip without a special API arg.
  stage: 'active',
  sort: 'customer_name',
  page: 1,
  pageSize: 20,
  includeArchived: false,
}

export function normalizeProjectSort(sort: string): string {
  const descending = sort.startsWith('-')
  const field = sort.replace(/^-/, '')
  const nativeField = [
    'name',
    'customer_name',
    'project_name',
    'created_at',
    'updated_at',
  ].includes(field)
    ? field
    : 'updated_at'
  return descending ? `-${nativeField}` : nativeField
}

export const useProjectsStore = defineStore('projects', () => {
  const projects = ref<ProjectListItem[]>([])
  const total = ref(0)
  const query = ref<ProjectsQuery>({ ...DEFAULT_PROJECTS_QUERY })
  const loading = ref(false)
  const error = ref<string | null>(null)
  const errorMeta = ref<{ kind: ErrorKind; technical: string } | null>(null)
  const errorKind = computed<ErrorKind>(() => errorMeta.value?.kind ?? 'user')
  const errorTechnical = computed<string | undefined>(() => errorMeta.value?.technical)
  // Per-id cache of full ProjectOut payloads from GET /projects/{id}. Keyed by
  // project id so multiple detail pages (or a return trip) reuse the cached
  // response until the next fetchProject() resolves.
  const detail = ref<Record<number, ProjectOut>>({})
  const overviewStats = ref<Record<number, ProjectOverviewStatsOut>>({})
  const overviewLoading = ref<Record<number, boolean>>({})
  const overviewError = ref<Record<number, string | null>>({})
  const overviewFetchSequences = new Map<number, number>()
  let overviewSessionGeneration = 0
  const customerSummary = ref<Record<number, CustomerProjectSummaryOut>>({})
  const customerAccess = ref<Record<number, CustomerAccessOut[]>>({})
  const customerAccessLoading = ref<Record<number, boolean>>({})
  const qaqcSettings = ref<Record<number, ProjectQaQcSettings>>({})
  const qaqcSettingsLoading = ref<Record<number, boolean>>({})
  const qaqcSettingsSaving = ref<Record<number, boolean>>({})
  const qaqcSettingsError = ref<Record<number, string | null>>({})
  const qaqcSettingsFetchSequences = new Map<number, number>()
  const qaqcSettingsMutationSequences = new Map<number, number>()
  let qaqcSettingsSessionGeneration = 0
  // Per-id loading flag for the detail fetch so the view can show a spinner
  // without conflating with the list-level `loading` field.
  const detailLoading = ref<Record<number, boolean>>({})
  let featureSupportSessionGeneration = 0
  const projectReadSequences = new Map<string, number>()
  const featureSupportMutationSequences = new Map<number, number>()
  const projectMutationQueues = new Map<number, Promise<ProjectOut>>()

  function nextProjectRead(projectId: number, kind: 'detail' | 'summary' = 'detail'): number {
    const key = `${projectId}:${kind}`
    const sequence = (projectReadSequences.get(key) ?? 0) + 1
    projectReadSequences.set(key, sequence)
    return sequence
  }

  function isCurrentProjectRead(
    projectId: number,
    sequence: number,
    generation: number,
    mutationSequence: number,
    kind: 'detail' | 'summary' = 'detail',
  ): boolean {
    return (
      featureSupportSessionGeneration === generation &&
      projectReadSequences.get(`${projectId}:${kind}`) === sequence &&
      (featureSupportMutationSequences.get(projectId) ?? 0) === mutationSequence
    )
  }

  function isCurrentQaQcFetch(
    projectId: number,
    generation: number,
    fetchSequence: number,
    mutationSequence?: number,
  ): boolean {
    return (
      qaqcSettingsSessionGeneration === generation &&
      qaqcSettingsFetchSequences.get(projectId) === fetchSequence &&
      (mutationSequence === undefined ||
        (qaqcSettingsMutationSequences.get(projectId) ?? 0) === mutationSequence)
    )
  }

  async function fetchProjects(
    override?: Partial<ProjectsQuery>,
    opts: { background?: boolean } = {},
  ): Promise<ProjectListItem[]> {
    if (override) {
      query.value = { ...query.value, ...override }
    }
    query.value.sort = normalizeProjectSort(query.value.sort)
    if (!opts.background) {
      loading.value = true
    }
    error.value = null
    errorMeta.value = null
    try {
      const current = query.value
      const response = await ProjectsService.listProjectsProjectsGet({
        search: current.search || undefined,
        stage: current.stage || undefined,
        sort: current.sort,
        page: current.page,
        pageSize: current.pageSize,
        includeArchived: current.includeArchived,
      })
      projects.value = response.items
      total.value = response.total
      return response.items
    } catch (err) {
      const classified = classifyApiError(err)
      error.value = classified.message
      errorMeta.value = { kind: classified.kind, technical: classified.technical }
      throw err
    } finally {
      loading.value = false
    }
  }

  /** Update one query dimension and re-fetch. Other dimensions are preserved. */
  async function updateQuery(patch: Partial<ProjectsQuery>): Promise<void> {
    // Changes to any dimension other than `page` itself reset to page 1 so
    // the caller doesn't see a blank page after narrowing the result set.
    const resetPage = Object.keys(patch).some((key) => key !== 'page')
    if (resetPage && patch.page === undefined) {
      patch.page = 1
    }
    await fetchProjects(patch)
  }

  // Fetch a single project's full detail payload (including stages and
  // aggregated task counts). Caches on success keyed by projectId. The list's
  // ProjectListItem is not refreshed here — `fetchProjects()` is the authority
  // for the dashboard row. Errors land on `error` and are re-thrown so views
  // can decide whether to toast or render a "not found" state.
  async function fetchProject(projectId: number): Promise<ProjectOut> {
    const sequence = nextProjectRead(projectId)
    const generation = featureSupportSessionGeneration
    const mutationSequence = featureSupportMutationSequences.get(projectId) ?? 0
    detailLoading.value = { ...detailLoading.value, [projectId]: true }
    error.value = null
    errorMeta.value = null
    try {
      const result = await ProjectsService.getProjectProjectsProjectIdGet({ projectId })
      if (isCurrentProjectRead(projectId, sequence, generation, mutationSequence))
        detail.value = { ...detail.value, [projectId]: result }
      return result
    } catch (err) {
      if (isCurrentProjectRead(projectId, sequence, generation, mutationSequence)) {
        const classified = classifyApiError(err)
        error.value = classified.message
        errorMeta.value = { kind: classified.kind, technical: classified.technical }
      }
      throw err
    } finally {
      if (
        generation === featureSupportSessionGeneration &&
        projectReadSequences.get(`${projectId}:detail`) === sequence
      ) {
        const next = { ...detailLoading.value }
        delete next[projectId]
        detailLoading.value = next
      }
    }
  }

  async function fetchOverviewStats(projectId: number): Promise<ProjectOverviewStatsOut> {
    const generation = overviewSessionGeneration
    const sequence = (overviewFetchSequences.get(projectId) ?? 0) + 1
    overviewFetchSequences.set(projectId, sequence)
    overviewLoading.value = { ...overviewLoading.value, [projectId]: true }
    overviewError.value = { ...overviewError.value, [projectId]: null }
    try {
      const result = await ProjectsService.getProjectOverviewStatsProjectsProjectIdOverviewStatsGet(
        {
          projectId,
        },
      )
      if (
        overviewSessionGeneration === generation &&
        overviewFetchSequences.get(projectId) === sequence
      ) {
        overviewStats.value = { ...overviewStats.value, [projectId]: result }
      }
      return result
    } catch (err) {
      if (
        overviewSessionGeneration === generation &&
        overviewFetchSequences.get(projectId) === sequence
      ) {
        overviewError.value = { ...overviewError.value, [projectId]: extractErrorMessage(err) }
      }
      throw err
    } finally {
      if (
        overviewSessionGeneration === generation &&
        overviewFetchSequences.get(projectId) === sequence
      ) {
        overviewLoading.value = { ...overviewLoading.value, [projectId]: false }
      }
    }
  }

  async function fetchCustomerSummary(projectId: number): Promise<CustomerProjectSummaryOut> {
    const sequence = nextProjectRead(projectId, 'summary')
    const generation = featureSupportSessionGeneration
    const mutationSequence = featureSupportMutationSequences.get(projectId) ?? 0
    const result =
      await ProjectsService.getCustomerProjectSummaryProjectsProjectIdCustomerSummaryGet({
        projectId,
      })
    if (!isCurrentProjectRead(projectId, sequence, generation, mutationSequence, 'summary'))
      return result
    customerSummary.value = { ...customerSummary.value, [projectId]: result }
    const current = detail.value[projectId]
    if (current)
      detail.value = { ...detail.value, [projectId]: { ...current, ...projectFeatures(result) } }
    return result
  }

  function featuresFor(projectId: number): ProjectFeatureSupport {
    return projectFeatures(
      detail.value[projectId] ??
        customerSummary.value[projectId] ??
        projects.value.find((project) => project.id === projectId),
    )
  }

  async function updateFeatureSupport(
    projectId: number,
    support: ProjectFeatureSupport,
  ): Promise<ProjectOut> {
    return updateProject(projectId, support)
  }

  async function updateProject(projectId: number, requestBody: ProjectPatch): Promise<ProjectOut> {
    return mutateProject(projectId, () =>
      ProjectsService.patchProjectProjectsProjectIdPatch({ projectId, requestBody }),
    )
  }

  async function archiveProject(projectId: number): Promise<ProjectOut> {
    return mutateProject(projectId, () =>
      ProjectsService.archiveProjectProjectsProjectIdArchivePost({ projectId }),
    )
  }

  async function restoreProject(projectId: number): Promise<ProjectOut> {
    return mutateProject(projectId, () =>
      ProjectsService.restoreProjectProjectsProjectIdRestorePost({ projectId }),
    )
  }

  async function mutateProject(
    projectId: number,
    mutation: () => Promise<ProjectOut>,
  ): Promise<ProjectOut> {
    const generation = featureSupportSessionGeneration
    const run = () => {
      if (generation !== featureSupportSessionGeneration)
        throw Object.assign(
          new Error('Your session changed. Sign in again before saving project changes.'),
          {
            status: 401,
            code: 'authentication_required',
          },
        )
      return applyProjectMutation(projectId, mutation, generation)
    }
    // A failed later operation must not discard an earlier successful response.
    // Independent project queues continue after failure and never cross sessions.
    const previous = projectMutationQueues.get(projectId)
    const pending = previous ? previous.then(run, run) : run()
    projectMutationQueues.set(projectId, pending)
    try {
      return await pending
    } finally {
      if (projectMutationQueues.get(projectId) === pending) projectMutationQueues.delete(projectId)
    }
  }

  async function applyProjectMutation(
    projectId: number,
    mutation: () => Promise<ProjectOut>,
    generation: number,
  ): Promise<ProjectOut> {
    const sequence = (featureSupportMutationSequences.get(projectId) ?? 0) + 1
    featureSupportMutationSequences.set(projectId, sequence)
    const loading = { ...detailLoading.value }
    delete loading[projectId]
    detailLoading.value = loading
    const result = await mutation()
    if (
      generation !== featureSupportSessionGeneration ||
      featureSupportMutationSequences.get(projectId) !== sequence
    )
      return result
    featureSupportMutationSequences.set(projectId, sequence + 1)
    detail.value = { ...detail.value, [projectId]: result }
    projects.value = projects.value.map((project) =>
      project.id === projectId ? { ...project, ...result } : project,
    )
    const summary = customerSummary.value[projectId]
    if (summary)
      customerSummary.value = {
        ...customerSummary.value,
        [projectId]: { ...summary, ...projectFeatures(result), display_name: result.name },
      }
    return result
  }

  function isDetailLoading(projectId: number): boolean {
    return Boolean(detailLoading.value[projectId])
  }

  async function updateCustomerRecipients(
    projectId: number,
    customerEmails: string[],
  ): Promise<CustomerAccessOut[]> {
    const result =
      await ProjectsService.updateCustomerRecipientsProjectsProjectIdCustomerAccessRecipientsPut({
        projectId,
        requestBody: { customer_emails: customerEmails },
      })
    customerAccess.value = { ...customerAccess.value, [projectId]: result }
    const current = detail.value[projectId]
    if (current) {
      detail.value = {
        ...detail.value,
        [projectId]: {
          ...current,
          customer_emails: customerEmails,
        },
      }
    }
    return result
  }

  async function fetchCustomerAccess(projectId: number): Promise<CustomerAccessOut[]> {
    customerAccessLoading.value = { ...customerAccessLoading.value, [projectId]: true }
    try {
      const result = await ProjectsService.listCustomerAccessProjectsProjectIdCustomerAccessGet({
        projectId,
      })
      customerAccess.value = { ...customerAccess.value, [projectId]: result }
      return result
    } finally {
      customerAccessLoading.value = { ...customerAccessLoading.value, [projectId]: false }
    }
  }

  async function revokeCustomerAccess(projectId: number, email: string): Promise<void> {
    await ProjectsService.revokeCustomerAccessProjectsProjectIdCustomerAccessRevokePost({
      projectId,
      requestBody: { email },
    })
    const current = detail.value[projectId]
    if (current) {
      detail.value = {
        ...detail.value,
        [projectId]: {
          ...current,
          customer_emails: (current.customer_emails || []).filter(
            (candidate) => candidate.toLowerCase() !== email.toLowerCase(),
          ),
        },
      }
    }
    await fetchCustomerAccess(projectId)
  }

  async function revokeCustomerAccessGlobally(projectId: number, email: string): Promise<void> {
    await AdminService.revokeCustomerAccessGloballyAdminCustomerAccessRevokePost({
      requestBody: { email },
    })
    const normalized = email.toLowerCase()
    detail.value = Object.fromEntries(
      Object.entries(detail.value).map(([id, project]) => [
        Number(id),
        {
          ...project,
          customer_emails: (project.customer_emails || []).filter(
            (candidate) => candidate.toLowerCase() !== normalized,
          ),
        },
      ]),
    )
    customerAccess.value = Object.fromEntries(
      Object.entries(customerAccess.value).map(([id, rows]) => [
        Number(id),
        rows.filter((row) => row.email.toLowerCase() !== normalized),
      ]),
    )
    await fetchCustomerAccess(projectId)
  }

  async function fetchQaQcSettings(projectId: number): Promise<ProjectQaQcSettings> {
    const generation = qaqcSettingsSessionGeneration
    const fetchSequence = (qaqcSettingsFetchSequences.get(projectId) ?? 0) + 1
    const mutationSequence = qaqcSettingsMutationSequences.get(projectId) ?? 0
    qaqcSettingsFetchSequences.set(projectId, fetchSequence)
    qaqcSettingsLoading.value = { ...qaqcSettingsLoading.value, [projectId]: true }
    qaqcSettingsError.value = { ...qaqcSettingsError.value, [projectId]: null }
    try {
      const result = await ProjectsService.getProjectQaqcSettingsProjectsProjectIdQaqcSettingsGet({
        projectId,
      })
      const current = qaqcSettings.value[projectId]
      if (
        isCurrentQaQcFetch(projectId, generation, fetchSequence, mutationSequence) &&
        (!current || result.qa_qc_settings_revision >= current.qa_qc_settings_revision)
      ) {
        qaqcSettings.value = { ...qaqcSettings.value, [projectId]: result }
      }
      return result
    } catch (err) {
      if (isCurrentQaQcFetch(projectId, generation, fetchSequence, mutationSequence)) {
        qaqcSettingsError.value = {
          ...qaqcSettingsError.value,
          [projectId]: extractErrorMessage(err),
        }
      }
      throw err
    } finally {
      if (isCurrentQaQcFetch(projectId, generation, fetchSequence)) {
        qaqcSettingsLoading.value = { ...qaqcSettingsLoading.value, [projectId]: false }
      }
    }
  }

  async function updateQaQcSettings(
    projectId: number,
    requestBody: ProjectQaQcSettingsUpdate,
  ): Promise<ProjectQaQcSettings> {
    const generation = qaqcSettingsSessionGeneration
    const mutationSequence = (qaqcSettingsMutationSequences.get(projectId) ?? 0) + 1
    qaqcSettingsMutationSequences.set(projectId, mutationSequence)
    qaqcSettingsFetchSequences.set(projectId, (qaqcSettingsFetchSequences.get(projectId) ?? 0) + 1)
    qaqcSettingsLoading.value = { ...qaqcSettingsLoading.value, [projectId]: false }
    qaqcSettingsSaving.value = { ...qaqcSettingsSaving.value, [projectId]: true }
    qaqcSettingsError.value = { ...qaqcSettingsError.value, [projectId]: null }
    try {
      const result =
        await ProjectsService.updateProjectQaqcSettingsProjectsProjectIdQaqcSettingsPut({
          projectId,
          requestBody,
        })
      if (
        qaqcSettingsSessionGeneration === generation &&
        qaqcSettingsMutationSequences.get(projectId) === mutationSequence
      ) {
        qaqcSettings.value = { ...qaqcSettings.value, [projectId]: result }
      }
      return result
    } catch (err) {
      if (
        qaqcSettingsSessionGeneration === generation &&
        qaqcSettingsMutationSequences.get(projectId) === mutationSequence
      ) {
        qaqcSettingsError.value = {
          ...qaqcSettingsError.value,
          [projectId]: extractErrorMessage(err),
        }
      }
      throw err
    } finally {
      if (
        qaqcSettingsSessionGeneration === generation &&
        qaqcSettingsMutationSequences.get(projectId) === mutationSequence
      ) {
        qaqcSettingsSaving.value = { ...qaqcSettingsSaving.value, [projectId]: false }
      }
    }
  }

  function $reset(): void {
    featureSupportSessionGeneration += 1
    projectReadSequences.clear()
    featureSupportMutationSequences.clear()
    projectMutationQueues.clear()
    overviewSessionGeneration += 1
    for (const [projectId, sequence] of overviewFetchSequences) {
      overviewFetchSequences.set(projectId, sequence + 1)
    }
    qaqcSettingsSessionGeneration += 1
    qaqcSettingsFetchSequences.clear()
    qaqcSettingsMutationSequences.clear()
    projects.value = []
    total.value = 0
    query.value = { ...DEFAULT_PROJECTS_QUERY }
    loading.value = false
    error.value = null
    errorMeta.value = null
    detail.value = {}
    overviewStats.value = {}
    overviewLoading.value = {}
    overviewError.value = {}
    customerSummary.value = {}
    customerAccess.value = {}
    customerAccessLoading.value = {}
    qaqcSettings.value = {}
    qaqcSettingsLoading.value = {}
    qaqcSettingsSaving.value = {}
    qaqcSettingsError.value = {}
    detailLoading.value = {}
  }

  return {
    projects,
    total,
    query,
    loading,
    error,
    errorKind,
    errorTechnical,
    detail,
    overviewStats,
    overviewLoading,
    overviewError,
    customerSummary,
    customerAccess,
    customerAccessLoading,
    qaqcSettings,
    qaqcSettingsLoading,
    qaqcSettingsSaving,
    qaqcSettingsError,
    // Pinia setup stores must return state refs for $subscribe/devtools tracking.
    detailLoading,
    isDetailLoading,
    fetchProjects,
    updateQuery,
    fetchProject,
    fetchOverviewStats,
    fetchCustomerSummary,
    featuresFor,
    updateFeatureSupport,
    updateProject,
    archiveProject,
    restoreProject,
    fetchCustomerAccess,
    updateCustomerRecipients,
    revokeCustomerAccess,
    revokeCustomerAccessGlobally,
    fetchQaQcSettings,
    updateQaQcSettings,
    $reset,
  }
})
